import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Button,
  Box,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel
} from "@mui/material";
import { axiosInstance } from "../../services/axiosInstance";
import { endpoints } from "../../services/endpoint";
import { CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const CreateMailbox = ({ open, handleClose, onMailboxCreated, existingMailboxes = [] }) => {
  const [name, setName] = useState("");
  const [type, setType] = useState("Agent");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const isValid = name.trim() !== "";

  const handleCreate = async () => {
    const trimmedName = name.trim();

    if (!trimmedName || loading) return;

    // ✅ Check duplicate (case insensitive)
    const isDuplicate = existingMailboxes.some(
      (mailbox) =>
        mailbox.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );

    if (isDuplicate) {
      setError("Mailbox with this name already exists.");
      return;
    }

    setError(""); // clear error if valid
    setLoading(true);
    const now = new Date();

    const formattedDate =
      String(now.getDate()).padStart(2, "0") +
      "-" +
      String(now.getMonth() + 1).padStart(2, "0") +
      "-" +
      now.getFullYear() +
      " " +
      String(now.getHours()).padStart(2, "0") +
      ":" +
      String(now.getMinutes()).padStart(2, "0") +
      ":" +
      String(now.getSeconds()).padStart(2, "0");

    const payload = {
      Name: trimmedName,
      Type: type.toLowerCase(),
      Created: formattedDate
    };

    try {
      const response = await axiosInstance.post(
        endpoints.voiceMailsDisplay.voiceMailList,
        payload
      );

      console.log("Create Mailbox Response:", response.data);

      onMailboxCreated({
        name: trimmedName,
        type: type,
        created: formattedDate
      });

      handleClose();
      setName("");
      setType("Agent");
      setError("");

    } catch (error) {
      console.error("Error creating mailbox:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          padding: 1
        }
      }}
    >
      {/* HEADER */}
      <DialogTitle sx={{ fontWeight: 600 }}>
        Creating a Mailbox
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* NAME FIELD */}
        <Box mt={1}>
          <Typography variant="body2" mb={1}>
            Name
          </Typography>

          <TextField
            fullWidth
            placeholder="e.g. John's Mailbox"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(""); // clear error while typing
            }}
            error={Boolean(error)}
            helperText={error}
          />
        </Box>

        {/* TYPE */}
        <Box mt={3}>
          <Typography variant="body2" mb={1}>
            Type
          </Typography>

          <RadioGroup
            row
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <FormControlLabel
              value="Agent"
              control={<Radio />}
              label="Agent"
            />
            <FormControlLabel
              value="Queue"
              control={<Radio />}
              label="Queue"
            />
          </RadioGroup>
        </Box>

        {/* INFO BOX */}
        <Box
          mt={3}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            backgroundColor: "#f3f4f6",
            padding: 2,
            borderRadius: 2,
            border: "1px solid #e5e7eb"
          }}
        >
          <InfoOutlinedIcon sx={{ color: "#6b7280" }} />
          <Typography variant="body2" color="text.secondary">
            You will automatically be given access to this Mailbox upon creation.
          </Typography>
        </Box>
      </DialogContent>

      {/* ACTIONS */}
      <DialogActions sx={{ padding: 2 }}>
        <Button
          variant="contained"
          disabled={!isValid}
          onClick={handleCreate}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            px: 3
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Create"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateMailbox;